import React, { createContext, useState, useEffect, useContext } from 'react';
import { auth } from '../Config/config';
import { 
  onAuthStateChanged, 
  signOut, 
  setPersistence, 
  browserLocalPersistence,
  inMemoryPersistence
} from 'firebase/auth';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const setupAuth = async () => {
      try {
        // Set persistence to local storage (this keeps the user logged in even after browser restart)
        await setPersistence(auth, browserLocalPersistence);

        const unsubscribe = onAuthStateChanged(auth, (user) => {
          if (user) {
            // Check if the user's login time is older than 2 months
            const twoMonthsAgo = new Date();
            twoMonthsAgo.setMonth(twoMonthsAgo.getMonth() - 2);

            if (user.metadata.lastSignInTime && new Date(user.metadata.lastSignInTime) < twoMonthsAgo) {
              // If older than 2 months, sign out the user
              signOut(auth);
            } else {
              setUser(user);
            }
          } else {
            setUser(null);
          }
          setLoading(false);
        });

        return () => unsubscribe();
      } catch (error) {
        console.error("Error setting up auth:", error);
        setLoading(false);
      }
    };

    setupAuth();
  }, []);

  const login = async (email, password) => {
    try {
      // Implement your login logic here
      // For example: await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      console.error("Error logging in:", error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await setPersistence(auth, inMemoryPersistence); // Switch to in-memory persistence before signing out
      await signOut(auth);
      setUser(null);
    } catch (error) {
      console.error("Error signing out: ", error);
    }
  };

  const value = {
    user,
    loading,
    login,
    logout,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};